import { type MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  ClickAwayListener,
  colors,
  Grow,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import LogoutIcon from '@mui/icons-material/Logout';

import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { getUserStore, type FrontendId } from '../../store/UserStore';
import { MenuButton } from './MenuButton';
import type { VkUser, YandexUser } from '../../types/user';

export default function UserMenuButton({
  isSmall = false,
  frontendId,
}: {
  isSmall?: boolean;
  frontendId: FrontendId;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const { t } = useTranslation();

  const { currentUser, resetUser } = getUserStore(frontendId);

  const [userName, userAvatar, userInitials] = useMemo(() => {
    if (!currentUser) return ['', undefined, ''];

    let userName = currentUser.name;
    let userAvatar = undefined;

    if (currentUser?.yandex_data) {
      const data: YandexUser = JSON.parse(currentUser.yandex_data);
      userName = data.real_name ?? data.login;
      if (!data.is_avatar_empty) {
        userAvatar = `https://avatars.yandex.net/get-yapic/${data.default_avatar_id}/islands-middle`;
      }
    } else if (currentUser?.vk_data) {
      const data: VkUser = JSON.parse(currentUser.vk_data);
      userName = `${data.first_name} ${data.last_name}`;
      userAvatar = data.avatar;
    }

    const initials = `${userName.split(' ')[0][0]}${userName.split(' ')?.[1]?.[0] || ''}`;

    return [userName, userAvatar, initials];
  }, [currentUser]);

  if (!currentUser) return null;

  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
      <Stack position={'relative'}>
        <MenuButton
          isSmall={isSmall}
          icon={
            <Tooltip
              title={
                <Stack gap={0.5}>
                  <Typography lineHeight={1}>{userName}</Typography>

                  <Typography variant="caption" lineHeight={1}>
                    {currentUser.login ?? ''}
                  </Typography>
                </Stack>
              }
            >
              <span>
                {userAvatar && <Avatar src={userAvatar} />}
                {!userAvatar && (
                  <Avatar sx={{ bgcolor: '#26393f', color: 'white' }}>{userInitials}</Avatar>
                )}
              </span>
            </Tooltip>
          }
          onClick={handleClick}
          content={
            <Stack gap={0.5} position={'relative'} overflow={'auto'}>
              <Typography
                whiteSpace={'nowrap'}
                fontWeight={500}
                lineHeight={1}
                overflow={'hidden'}
                textOverflow={'ellipsis'}
              >
                {userName}
              </Typography>

              <Typography
                variant="caption"
                color="grey"
                whiteSpace={'nowrap'}
                lineHeight={1}
                overflow={'hidden'}
                textOverflow={'ellipsis'}
              >
                {currentUser.login ?? ''}
              </Typography>
            </Stack>
          }
          StackProps={{ px: isSmall ? 1 : 2 }}
        />

        <Grow in={!!anchorEl}>
          <Paper
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: '100%',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
              border: `1px solid ${colors.grey[200]}`,
              pb: 1,
            }}
          >
            <ListItem>
              <ListItemIcon>
                <TranslateIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <LanguageSwitcher
                    selectProps={{ variant: 'standard' }}
                    showLabel={false}
                    frontendId={frontendId}
                  />
                }
              />
            </ListItem>

            <ListItemButton onClick={resetUser}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t('login.logout')}
            </ListItemButton>
          </Paper>
        </Grow>
      </Stack>
    </ClickAwayListener>
  );
}
